async function sharePngBlob(blob, filename, title) {
  if (!blob) return false;
  const file = new File([blob], filename, { type: 'image/png' });
  try {
    if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title, text: title });
      return true;
    }
  } catch (err) {
    if (err?.name === 'AbortError') return true;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function fillRoundRect(ctx, x, y, w, h, r, fill) {
  roundRect(ctx, x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
}

function drawHeader(ctx, width, title, subtitle) {
  fillRoundRect(ctx, 0, 0, width, 720, 0, '#0d0d0e');
  ctx.fillStyle = '#FF6B00';
  ctx.font = '700 22px Barlow, Helvetica, sans-serif';
  ctx.fillText('LIFTLAB', 40, 48);
  ctx.fillStyle = '#f2f2f3';
  ctx.font = '800 34px Barlow Condensed, Helvetica, sans-serif';
  ctx.fillText(title, 40, 96);
  ctx.fillStyle = '#a1a1a6';
  ctx.font = '500 18px Barlow, Helvetica, sans-serif';
  ctx.fillText(subtitle, 40, 124);
}

export async function shareWorkoutCard({ date, dayName, exercises, volume, durationLabel, vsLabel, language }) {
  const rows = (exercises || []).slice(0, 10).map((ex) => {
    const logged = (ex.sets || []).filter((s) => s.completed !== false && ((s.reps && s.weight) || s.durationMinutes));
    const sets = logged.length || (ex.sets || []).length;
    const best = logged.reduce((max, s) => Math.max(max, parseFloat(s.weight) || 0), 0);
    return { name: ex.name, sets, best };
  });
  const height = (vsLabel ? 204 : 180) + rows.length * 52 + 80;
  const width = 720;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.max(height, 420);
  const ctx = canvas.getContext('2d');
  drawHeader(ctx, width, language === 'da' ? 'Træning' : 'Workout', date + (dayName ? ` · ${dayName}` : ''));

  const meta = [];
  if (volume) meta.push(`${Math.round(volume).toLocaleString()} ${language === 'da' ? 'volume' : 'volume'}`);
  if (durationLabel) meta.push(durationLabel);
  ctx.fillStyle = '#FF6B00';
  ctx.font = '700 16px Barlow, Helvetica, sans-serif';
  ctx.fillText(meta.join('  ·  '), 40, 158);

  let rowsTop = 190;
  if (vsLabel) {
    ctx.fillStyle = '#a1a1a6';
    ctx.font = '600 15px Barlow, Helvetica, sans-serif';
    ctx.fillText(vsLabel, 40, 182);
    rowsTop = 214;
  }

  rows.forEach((row, i) => {
    const y = rowsTop + i * 52;
    fillRoundRect(ctx, 32, y - 24, width - 64, 46, 10, '#161618');
    ctx.fillStyle = '#f2f2f3';
    ctx.font = '700 18px Barlow, Helvetica, sans-serif';
    ctx.fillText(row.name, 48, y + 4);
    ctx.fillStyle = '#a1a1a6';
    ctx.font = '600 14px Barlow, Helvetica, sans-serif';
    const right = row.best ? `${row.sets} sets · ${row.best}` : `${row.sets} sets`;
    ctx.fillText(right, width - 48 - ctx.measureText(right).width, y + 4);
  });

  ctx.fillStyle = '#6e6e73';
  ctx.font = '500 13px Barlow, Helvetica, sans-serif';
  ctx.fillText('liftlab', 40, canvas.height - 28);

  const blob = await canvasToBlob(canvas);
  const title = language === 'da' ? `LiftLab · ${date}` : `LiftLab · ${date}`;
  return sharePngBlob(blob, `liftlab-${date.replace(/\//g, '-')}.png`, title);
}

export async function shareStatsCard({ title, subtitle, volumeLabel, changeLabel, bars, language }) {
  const width = 720;
  const height = 560;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  drawHeader(ctx, width, title, subtitle);

  fillRoundRect(ctx, 32, 150, width - 64, 90, 14, '#161618');
  ctx.fillStyle = '#6e6e73';
  ctx.font = '600 12px Barlow, Helvetica, sans-serif';
  ctx.fillText(language === 'da' ? 'UGENTLIG VOLUME' : 'WEEKLY VOLUME', 52, 178);
  ctx.fillStyle = '#f2f2f3';
  ctx.font = '800 32px Barlow Condensed, Helvetica, sans-serif';
  ctx.fillText(volumeLabel, 52, 218);
  if (changeLabel) {
    ctx.fillStyle = '#FF6B00';
    ctx.font = '700 16px Barlow, Helvetica, sans-serif';
    ctx.fillText(changeLabel, 52, 224 + 18);
  }

  const max = Math.max(1, ...(bars || []).map((b) => b.value));
  const list = (bars || []).slice(0, 6);
  list.forEach((bar, i) => {
    const y = 270 + i * 40;
    ctx.fillStyle = '#a1a1a6';
    ctx.font = '600 14px Barlow, Helvetica, sans-serif';
    ctx.fillText(bar.label, 40, y + 12);
    const trackX = 180;
    const trackW = width - 240;
    fillRoundRect(ctx, trackX, y, trackW, 14, 7, '#1c1c1f');
    fillRoundRect(ctx, trackX, y, Math.max(8, (bar.value / max) * trackW), 14, 7, '#FF6B00');
    ctx.fillStyle = '#f2f2f3';
    ctx.font = '700 13px Barlow, Helvetica, sans-serif';
    ctx.fillText(String(bar.value), trackX + trackW + 12, y + 12);
  });

  ctx.fillStyle = '#6e6e73';
  ctx.font = '500 13px Barlow, Helvetica, sans-serif';
  ctx.fillText('liftlab', 40, height - 28);

  const blob = await canvasToBlob(canvas);
  return sharePngBlob(blob, 'liftlab-stats.png', title);
}
